import type { AccessControlProvider } from '@refinedev/core'

import { hasPermission } from '@/auth/capabilities'

import { authProvider } from './authProvider'

const GPU_GUARD = {
  scope: 'admin:gpu',
  reason: 'You do not have permission to access the GPU service.',
}

const GUARDED: Record<string, { scope: string; reason: string }> = {
  roles: {
    scope: 'admin:roles',
    reason: 'You do not have permission to access role management.',
  },
  images: {
    scope: 'admin:default_images',
    reason: 'You do not have permission to access the image catalog.',
  },
  'gpu-section': GPU_GUARD,
  'gpu-providers': GPU_GUARD,
  'gpu-instances': GPU_GUARD,
  'gpu-reports': GPU_GUARD,
  'gpu-akash-blocks': GPU_GUARD,
}

export function requiredPermission(scope: string, action: string | undefined) {
  if (action === 'list' || action === 'show') return `${scope}:read`
  return `${scope}:write`
}

export function canAccessResource(
  permissions: readonly string[],
  resource: string | undefined,
  action: string | undefined,
): boolean {
  const guard = resource ? GUARDED[resource] : undefined
  return (
    !guard ||
    hasPermission(permissions, requiredPermission(guard.scope, action))
  )
}

export const accessControlProvider: AccessControlProvider = {
  can: async ({ resource, action }) => {
    const guard = resource ? GUARDED[resource] : undefined
    if (!guard) return { can: true }

    const permissions = ((await authProvider.getPermissions?.()) ??
      []) as string[]
    return {
      can: canAccessResource(permissions, resource, action),
      reason: guard.reason,
    }
  },
  options: {
    buttons: { enableAccessControl: true, hideIfUnauthorized: true },
  },
}
